/**
 * External Imports
 */
import { FC, useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';

/**
 * Nexus Imports
 */
import { NeuIcon, NeuButton } from '@neutron/react';

/**
 * Internal Imports
 */
import EmployeeSearch from '../reusableModalPieces/employeeSearch';

import { RootState } from '../../../redux/store';
import {
  useGetEmployeeConfigQuery,
  usePostAddDelegateToUserMutation,
  usePostAddEmployeeToUserFavsMutation,
  usePostRemoveDelegateFromUserMutation,
  usePostRemoveEmployeeFromUserFavsMutation
} from '../../../redux/apis/Users.api';

import { handleModal } from '../../../services/ModalPortal/utils';

/**
 * Global Type Definition Imports
 */
import { AuthUserProps, Employee } from '../../../config/interfaces';

/**
 * Style Imports
 */
import {
  // AddSelectedEmployeeButton,
  CardHeader,
  EmployeeNameInTable,
  InputHeading,
  ListContainer,
  ListRow,
  ListHeader,
  ModalContainer,
  ModalCardContent,
  ModalFooter,
  RemoveButton,
  TextInTable
} from './EmployeeRoundingSettingsModal.styles';

interface ModalProps {
  authorizedUser: AuthUserProps;
  modalType: string;
}

// CAN BE USED FOR Add Employees, Add Delegates,and Edit Rounding Access.
const EmployeeRoundingSettingsModal: FC<ModalProps> = ({
  authorizedUser,
  modalType
}) => {
  const [listToDisplay, setListToDisplay] = useState<Employee[]>([]);
  // TODO: possible need when upgrading neutron 3
  // const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
  //   null
  // );
  const modalContentRef = useRef<HTMLNeuCardElement>(null);

  const {
    data: {
      favEmployees: favEmployeesList = [],
      delegatedToEmployees: delegatedToList = []
    } = {}
    // isError: isGetEmployeeConfigError,
    // isFetching: isGetEmployeeConfigFetching,
    // isLoading: isGetEmployeeConfigLoading
  } = useGetEmployeeConfigQuery(authorizedUser?.hcaid ?? '', {
    refetchOnMountOrArgChange: true
  });
  const [
    addEmployeeToUserFavs
    // {
    //   isError: isAddEmployeeToUserFavsError,
    //   isLoading: isAddEmployeeToUserFavsLoading,
    //   isSuccess: isAddEmployeeToUserFavsSuccess
    // }
  ] = usePostAddEmployeeToUserFavsMutation();
  const [
    removeEmployeeFromUserFavs
    // {
    //   isError: isRemoveEmployeeFromUserFavsError,
    //   isLoading: isRemoveEmployeeFromUserFavsLoading,
    //   isSuccess: isRemoveEmployeeFromUserFavsSuccess
    // }
  ] = usePostRemoveEmployeeFromUserFavsMutation();
  const [
    addDelegateToUser
    // {
    //   isError: isAddDelegateToUserError,
    //   isLoading: isAddDelegateToUserLoading,
    //   isSuccess: isAddDelegateToUserSuccess
    // }
  ] = usePostAddDelegateToUserMutation();
  const [
    removeDelegateFromUser
    // {
    //   isError: isRemoveDelegateFromUserError,
    //   isLoading: isRemoveDelegateFromUserLoading,
    //   isSuccess: isRemoveDelegateFromUserSuccess
    // }
  ] = usePostRemoveDelegateFromUserMutation();

  const handleEmployeeSearchSelected = async (employee: Employee) => {
    // existing users are not allowed to add
    if (modalType === 'Add Employees') {
      if (
        // existing user not allowed
        !favEmployeesList?.some(
          (existingUser: Employee) => existingUser.hcaid === employee.hcaid
        ) &&
        // authorized user not allowed
        employee.hcaid !== authorizedUser.hcaid
      ) {
        const payload = {
          userId: authorizedUser.hcaid,
          employeeId: employee.hcaid
        };
        await addEmployeeToUserFavs(payload);
      }
    }

    if (modalType === 'Add Delegates') {
      if (
        // existing user not allowed
        !delegatedToList?.some(
          (existingUser: Employee) => existingUser.hcaid === employee.hcaid
        ) &&
        // authorized user not allowed
        employee.hcaid !== authorizedUser.hcaid
      ) {
        const payload = {
          userId: authorizedUser.hcaid,
          delegateIds: [employee.hcaid]
        };
        await addDelegateToUser(payload);
      }
    }
  };

  // TODO: possible need for neutron 3 update
  // const handleAddEmployeeSearchSelected = () => {
  //   if (selectedEmployee) {
  //     handleEmployeeSearchSelected(selectedEmployee);
  //   }
  // };

  const handleRemoveEmployee = async (employee: Employee) => {
    if (modalType === 'Add Employees') {
      const payload = {
        userId: authorizedUser.hcaid,
        employeeId: employee.hcaid
      };
      await removeEmployeeFromUserFavs(payload);
    }
    if (modalType === 'Add Delegates') {
      const payload = {
        userId: authorizedUser.hcaid,
        delegateId: employee.hcaid
      };
      await removeDelegateFromUser(payload);
    }
  };

  // Sorting the list A-Z for initial display
  useEffect(() => {
    if (favEmployeesList && modalType === 'Add Employees') {
      const sorted = [...favEmployeesList].sort((a: Employee, b: Employee) =>
        a.firstName > b.firstName ? 1 : -1
      );
      setListToDisplay(sorted);
    }

    if (favEmployeesList && modalType === 'Add Delegates') {
      const sorted = [...delegatedToList].sort((a: Employee, b: Employee) =>
        a.firstName > b.firstName ? 1 : -1
      );
      setListToDisplay(sorted);
    }
  }, [favEmployeesList, delegatedToList, authorizedUser]);

  return (
    <ModalContainer
      id="Employee-Rounding-Settings-Modal"
      className="show-modal"
      unit="card"
      ref={modalContentRef}
    >
      <CardHeader unit="header" className="neu-bg-plain-0">
        {modalType === 'Add Employees' ? (
          <p className="mb-0">Add Employees</p>
        ) : modalType === 'Add Delegates' ? (
          <p className="mb-0">Add Delegates</p>
        ) : null}

        <NeuButton
          id="Employee-Rounding-Settings-Modal-Close-Button"
          aria-label="close panel"
          color="blue"
          icon="close"
          kind="tertiary"
          size="small"
          onClick={() => handleModal(modalContentRef)}
        >
          Close
        </NeuButton>
      </CardHeader>
      <ModalCardContent unit="content">
        {
          modalType === 'Add Employees' || modalType === 'Add Delegates' ? (
            <>
              <InputHeading>
                {modalType === 'Add Employees' ? 'Employee' : 'Delegate'}
              </InputHeading>
              <EmployeeSearch
                showLabel={false}
                val={(favEmployeesList ?? []).map(
                  (emp: Employee) => emp.employeeId
                )}
                handleRemoveEmployee={() => {}}
                setEmployeeSelection={(emp: Employee) =>
                  handleEmployeeSearchSelected(emp)
                }
              />

              {modalType === 'Add Employees' ||
              modalType === 'Add Delegates' ? (
                <>
                  <ListHeader className="mt-2">
                    {modalType === 'Add Employees'
                      ? 'My Employees'
                      : 'My Delegates'}
                  </ListHeader>
                  <ListContainer>
                    {listToDisplay.map((employee: Employee) => {
                      return (
                        <ListRow key={employee.hcaid}>
                          <div style={{ width: '33%' }}>
                            <EmployeeNameInTable
                              style={{ fontWeight: '700' }}
                            >{`${employee.firstName} ${employee.lastName}`}</EmployeeNameInTable>

                            <TextInTable>{employee.hcaid}</TextInTable>
                          </div>
                          <div style={{ width: '46%' }}>
                            <TextInTable>{employee.title}</TextInTable>
                            <TextInTable>{employee.dept}</TextInTable>
                          </div>
                          <RemoveButton
                            onClick={() => handleRemoveEmployee(employee)}
                          >
                            <NeuIcon color="red">remove_circle</NeuIcon> {` `}
                            Remove
                          </RemoveButton>
                        </ListRow>
                      );
                    })}
                  </ListContainer>
                </>
              ) : (
                <div style={{ marginTop: '25px' }}>
                  <ListHeader>My Delegates</ListHeader>
                </div>
              )}
            </>
          ) : null // Add the content of Rounding Access modal here
        }
      </ModalCardContent>
      <ModalFooter unit="footer">
        {/* <AddSelectedEmployeeButton
        data-testid="Add-Adhoc-Employee-Button"
        onClick={handleAddEmployeeSearchSelected}
      >
        Add Employee
      </AddSelectedEmployeeButton> */}
      </ModalFooter>
    </ModalContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUser: state.AuthorizedUser.authorizedUser,
    modalType: state.ModalReducer.modalType
  };
};

export default connect(mapReduxStateToProps)(EmployeeRoundingSettingsModal);
